import { useRef } from 'react';
import type { ISectionPerformanceKPI } from '@services/types';

import { E_DEVICE_TYPE } from '@common/configs';
import { classesBuilder, formatNumber } from '@common/helpers';
import Card from '@components/Card';
import DeviceFrame from '@components/DeviceFrame';
import { useSharedDeviceScale } from '@components/DeviceFrame/hooks';
import Icon from '@components/Icon';
import type { ISectionComponentProps } from '@components/ReportContent/types';

import '../section.scss';
import style from './styles.module.scss';

const PerformanceSection = ({ sectionData }: ISectionComponentProps) => {
  const sharedContainerRef = useRef<HTMLDivElement>(null);
  const { desktop, mobile } = useSharedDeviceScale(sharedContainerRef);

  if (!('data' in sectionData) || !sectionData.data) {
    return <div>Error: Invalid data for Performance section.</div>;
  }

  const { data, images } = sectionData as ISectionPerformanceKPI;

  return (
    <div className={classesBuilder('section-wrapper', style['performance-wrapper'])}>
      <div className="section-header">
        <h3 className="section-subtitle">Campaign Review</h3>
        <h1 className="section-title">
          PERFORMANCE OVERALL <mark>(KPI Summary)</mark>
        </h1>
      </div>

      {(images?.kpi_summary_device_mobile || images?.kpi_summary_device_desktop) && (
        <div className="section-image">
          <div
            className={style['preview-wrapper']}
            ref={sharedContainerRef}
          >
            {images.kpi_summary_device_desktop && (
              <div className={style['device-container']}>
                <DeviceFrame
                  deviceType={E_DEVICE_TYPE.DESKTOP}
                  imageSrc={images.kpi_summary_device_desktop}
                  sharedScale={desktop}
                />
              </div>
            )}
            {images.kpi_summary_device_mobile && (
              <div className={classesBuilder(style['device-container'], style['mobile-wrapper'])}>
                <DeviceFrame
                  deviceType={E_DEVICE_TYPE.MOBILE}
                  imageSrc={images.kpi_summary_device_mobile}
                  sharedScale={mobile}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className={classesBuilder('section-data', style['performance-data'])}>
        <Card>
          <section className={style['kpi-block']}>
            <h2 className={style['performance-header-title']}>Impressions & Reach</h2>
            <div className={style['kpi-metrics']}>
              <div className={style['metric-item']}>
                <div className={style['kpi-metric-headline']}>
                  <Icon name="eye" />
                  <span className={style['kpi-metric-label']}>Ad Impressions</span>
                </div>
                <p className={style['kpi-metric-value']}>{formatNumber(data.ad_impressions)}</p>
              </div>
              <div className={style['metric-item']}>
                <div className={style['kpi-metric-headline']}>
                  <Icon name="team" />
                  <span className={style['kpi-metric-label']}>Unique Users</span>
                </div>
                <p className={style['kpi-metric-value']}>{formatNumber(data.unique_users)}</p>
              </div>
            </div>
          </section>
        </Card>
        <Card>
          <section className={style['kpi-block']}>
            <h2 className={style['performance-header-title']}>Clicks</h2>
            <div className={style['kpi-metrics']}>
              <div className={style['metric-item']}>
                <div className={style['kpi-metric-headline']}>
                  <Icon name="statistics" />
                  <span className={style['kpi-metric-label']}>Click-Through Rate</span>
                </div>
                <p className={style['kpi-metric-value']}>{formatNumber(data.ctr, { style: 'percent' })}</p>
              </div>
              <div className={style['metric-item']}>
                <div className={style['kpi-metric-headline']}>
                  <Icon name="click" />
                  <span className={style['kpi-metric-label']}>Clicks</span>
                </div>
                <p className={style['kpi-metric-value']}>{formatNumber(data.clicks)}</p>
              </div>
            </div>
          </section>
        </Card>
        <Card>
          <section className={style['kpi-block']}>
            <h2 className={style['performance-header-title']}>Viewability 50/1</h2>
            <div className={style['kpi-metrics']}>
              <div className={style['metric-item']}>
                <div className={style['kpi-metric-headline']}>
                  <Icon name="statistics" />
                  <span className={style['kpi-metric-label']}>Viewability Rate</span>
                </div>
                <p className={style['kpi-metric-value']}>{formatNumber(data.viewability_rate, { style: 'percent' })}</p>
              </div>
              <div className={style['metric-item']}>
                <div className={style['kpi-metric-headline']}>
                  <Icon name="eye" />
                  <span className={style['kpi-metric-label']}>Viewability Impressions</span>
                </div>
                <p className={style['kpi-metric-value']}>{formatNumber(data.viewable_impressions)}</p>
              </div>
            </div>
          </section>
        </Card>
      </div>
    </div>
  );
};

export default PerformanceSection;
